import React, { useState, useMemo } from 'react';
import { Home, Save, TrendingUp, DollarSign, Calendar, PieChart, FileText, RefreshCw, Tag, ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { calculatePayment } from '../../utils/calculations';
import AmortizationSchedule from '../AmortizationSchedule';
import CompareScenarios from '../CompareScenarios';

const DetailsTab = ({ mortgage, saveMortgage, offers = [] }) => {
  const [formData, setFormData] = useState({
    lender: mortgage.lender || '',
    initial_balance: mortgage.initial_balance || '',
    amortization: mortgage.amortization || 25,
    amortization_remain: mortgage.amortization_remain || 20,
    current_balance: mortgage.current_balance || '',
    current_rate: mortgage.current_rate || '',
    home_value: mortgage.home_value || '',
    renewal_date: mortgage.renewal_date || '',
    payment_frequency: mortgage.payment_frequency || 'monthly',
    property_taxes: mortgage.property_taxes || '',
    job_loss_insurance: mortgage.job_loss_insurance || '',
    primary_residence: mortgage.primary_residence || 'yes'
  });
  
  const [saving, setSaving] = useState(false);
  const [showSummary, setShowSummary] = useState(!!mortgage.current_balance);
  const [showCompare, setShowCompare] = useState(false);
  const [isFormCollapsed, setIsFormCollapsed] = useState(!!mortgage.current_balance);
  
  // Calculate mortgage metrics
  const mortgageMetrics = useMemo(() => {
    const balance = parseFloat(formData.current_balance) || 0;
    const rate = parseFloat(formData.current_rate) || 0;
    const yearsRemain = parseInt(formData.amortization_remain) || 0;
    const homeValue = parseFloat(formData.home_value) || 0;
    const propertyTaxes = parseFloat(formData.property_taxes) || 0;
    const insurance = parseFloat(formData.job_loss_insurance) || 0;
    
    if (!balance || !rate || !yearsRemain) {
      return null;
    }
    
    // Calculate payment
    const paymentData = calculatePayment(balance, rate, yearsRemain, formData.payment_frequency);
    const monthlyPayment = paymentData.monthlyEquivalent;
    
    // Calculate total payments and interest
    const periodsPerYear = formData.payment_frequency === 'monthly' ? 12 : formData.payment_frequency === 'biweekly' ? 26 : 52;
    const totalPayments = yearsRemain * periodsPerYear;
    const totalPaid = paymentData.amount * totalPayments;
    const totalInterest = totalPaid - balance;
    
    // Principal paid so far (from initial to current)
    const initialBalance = parseFloat(formData.initial_balance) || balance;
    const principalPaid = initialBalance - balance;
    
    // Equity and LTV
    const equity = homeValue - balance;
    const ltv = homeValue > 0 ? (balance / homeValue) * 100 : 0;
    
    // Projected payoff date
    const today = new Date();
    const payoffDate = new Date(today.setFullYear(today.getFullYear() + yearsRemain));
    
    // Monthly total including taxes and insurance
    const totalMonthlyPayment = monthlyPayment + propertyTaxes + insurance;
    
    // Annual costs
    const annualMortgagePayment = monthlyPayment * 12;
    const annualPropertyTaxes = propertyTaxes * 12;
    const annualInsurance = insurance * 12;
    const totalAnnualCost = annualMortgagePayment + annualPropertyTaxes + annualInsurance;
    
    return {
      paymentAmount: paymentData.amount,
      monthlyPayment,
      totalMonthlyPayment,
      totalPaid,
      totalInterest,
      principalPaid,
      equity,
      ltv,
      payoffDate,
      totalPayments,
      annualMortgagePayment,
      annualPropertyTaxes,
      annualInsurance,
      totalAnnualCost,
      interestRate: rate,
      yearsRemaining: yearsRemain
    };
  }, [formData]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    // Convert string inputs to numbers
    const dataToSave = {
      ...formData,
      initial_balance: parseFloat(formData.initial_balance) || 0,
      amortization: parseInt(formData.amortization) || 25,
      amortization_remain: parseInt(formData.amortization_remain) || 20,
      current_balance: parseFloat(formData.current_balance) || 0,
      current_rate: parseFloat(formData.current_rate) || 0,
      home_value: parseFloat(formData.home_value) || 0,
      property_taxes: parseFloat(formData.property_taxes) || 0,
      job_loss_insurance: parseFloat(formData.job_loss_insurance) || 0
    };
    
    const success = await saveMortgage(dataToSave);
    setSaving(false);
    
    if (success) {
      setShowSummary(true);
      setIsFormCollapsed(true);
      toast.success('Mortgage details saved successfully!');
    }
  };
  
  return (
    <div data-testid="details-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-heading font-semibold flex items-center gap-2">
          <Home className="h-6 w-6 text-primary" />
          Current Mortgage Details
        </h2>
      </div>
      
      {/* Collapsible Form Section */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {/* Collapse Header - Always visible when form has data */}
        {showSummary && (
          <button
            onClick={() => setIsFormCollapsed(!isFormCollapsed)}
            data-testid="toggle-form-btn"
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{formData.lender || 'Your Mortgage'}</span>
                <span>•</span>
                <span>${parseFloat(formData.current_balance || 0).toLocaleString('en-CA')}</span>
                <span>•</span>
                <span>{formData.current_rate}%</span>
                {formData.renewal_date && (
                  <>
                    <span>•</span>
                    <span>Renews {new Date(formData.renewal_date).toLocaleDateString('en-CA', { month: 'short', year: 'numeric' })}</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isFormCollapsed && (
                <span className="text-xs text-primary flex items-center gap-1">
                  <Pencil className="h-3 w-3" />
                  Edit
                </span>
              )}
              {isFormCollapsed ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronUp className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </button>
        )}
        
        {/* Form Content */}
        <div className={`transition-all duration-300 ease-in-out ${isFormCollapsed && showSummary ? 'max-h-0 overflow-hidden' : 'max-h-[2000px]'}`}>
          <form onSubmit={handleSubmit} className="p-6 space-y-6 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1.5">Lender</label>
            <input
              type="text"
              name="lender"
              value={formData.lender}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="e.g., TD Bank"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1.5">Initial Mortgage Amount (CAD $)</label>
            <input
              type="number"
              name="initial_balance"
              value={formData.initial_balance}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="500000"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1.5">Initial Amortization (Years)</label>
            <input
              type="number"
              name="amortization"
              value={formData.amortization}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="25"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1.5">Remaining Amortization (Years)</label>
            <input
              type="number"
              name="amortization_remain"
              value={formData.amortization_remain}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="20"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1.5">Current Balance (CAD $)</label>
            <input
              type="number"
              name="current_balance"
              value={formData.current_balance}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="400000"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1.5">Current Rate (%)</label>
            <input
              type="number"
              step="0.01"
              name="current_rate"
              value={formData.current_rate}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="4.99"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1.5">Current Home Value (CAD $)</label>
            <input
              type="number"
              name="home_value"
              value={formData.home_value}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="600000"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1.5">Renewal Date</label>
            <input
              type="date"
              name="renewal_date"
              value={formData.renewal_date}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1.5">Payment Frequency</label>
            <select
              name="payment_frequency"
              value={formData.payment_frequency}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="monthly">Monthly (12 payments/year)</option>
              <option value="biweekly">Bi-weekly (26 payments/year)</option>
              <option value="weekly">Weekly (52 payments/year)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1.5">Monthly Property Taxes (CAD $)</label>
            <input
              type="number"
              step="0.01"
              name="property_taxes"
              value={formData.property_taxes}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="300"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1.5">Monthly Job Loss Insurance (CAD $)</label>
            <input
              type="number"
              step="0.01"
              name="job_loss_insurance"
              value={formData.job_loss_insurance}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="50"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1.5">Primary Residence?</label>
            <select
              name="primary_residence"
              value={formData.primary_residence}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>
        
        <div className="flex justify-end">
          <button
            type="submit"
            data-testid="save-mortgage-btn"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Details'}
          </button>
        </div>
      </form>
        </div>
      </div>
      
      {/* Mortgage Summary */}
      {showSummary && mortgageMetrics && (
        <div className="space-y-6 mt-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-heading font-semibold">Mortgage Summary</h3>
            <span className="text-sm text-muted-foreground">
              Based on your current details
            </span>
          </div>
          
          {/* Key Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Monthly Payment */}
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-4 w-4 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">Total Monthly Payment</p>
              </div>
              <p className="text-2xl font-bold font-mono">
                ${mortgageMetrics.totalMonthlyPayment.toLocaleString('en-CA', {maximumFractionDigits: 2})}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Mortgage: ${mortgageMetrics.monthlyPayment.toLocaleString('en-CA', {maximumFractionDigits: 2})}
              </p>
            </div>
            
            {/* Interest Paid */}
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 bg-red-500/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-red-400" />
                </div>
                <p className="text-sm text-muted-foreground">Total Interest</p>
              </div>
              <p className="text-2xl font-bold font-mono">
                ${mortgageMetrics.totalInterest.toLocaleString('en-CA', {maximumFractionDigits: 0})}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Over {mortgageMetrics.yearsRemaining} years
              </p>
            </div>
            
            {/* Principal Paid */}
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 bg-green-500/10 rounded-lg flex items-center justify-center">
                  <PieChart className="h-4 w-4 text-green-400" />
                </div>
                <p className="text-sm text-muted-foreground">Principal Paid</p>
              </div>
              <p className="text-2xl font-bold font-mono">
                ${mortgageMetrics.principalPaid.toLocaleString('en-CA', {maximumFractionDigits: 0})}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                To date
              </p>
            </div>
            
            {/* Home Equity */}
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <Home className="h-4 w-4 text-blue-400" />
                </div>
                <p className="text-sm text-muted-foreground">Home Equity</p>
              </div>
              <p className="text-2xl font-bold font-mono">
                ${mortgageMetrics.equity.toLocaleString('en-CA', {maximumFractionDigits: 0})}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                LTV: {mortgageMetrics.ltv.toFixed(1)}%
              </p>
            </div>
          </div>
          
          {/* Detailed Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Payment Breakdown */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h4 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" />
                Payment Breakdown
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Mortgage Payment ({formData.payment_frequency})</span>
                  <span className="font-mono font-medium">
                    ${mortgageMetrics.paymentAmount.toLocaleString('en-CA', {maximumFractionDigits: 2})}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Monthly Equivalent</span>
                  <span className="font-mono font-medium">
                    ${mortgageMetrics.monthlyPayment.toLocaleString('en-CA', {maximumFractionDigits: 2})}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Property Taxes (monthly)</span>
                  <span className="font-mono font-medium">
                    ${parseFloat(formData.property_taxes || 0).toLocaleString('en-CA', {maximumFractionDigits: 2})}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Insurance (monthly)</span>
                  <span className="font-mono font-medium">
                    ${parseFloat(formData.job_loss_insurance || 0).toLocaleString('en-CA', {maximumFractionDigits: 2})}
                  </span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between font-semibold">
                  <span>Total Monthly</span>
                  <span className="font-mono text-primary">
                    ${mortgageMetrics.totalMonthlyPayment.toLocaleString('en-CA', {maximumFractionDigits: 2})}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Annual Costs */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h4 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Annual Costs
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Mortgage Payments</span>
                  <span className="font-mono font-medium">
                    ${mortgageMetrics.annualMortgagePayment.toLocaleString('en-CA', {maximumFractionDigits: 0})}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Property Taxes</span>
                  <span className="font-mono font-medium">
                    ${mortgageMetrics.annualPropertyTaxes.toLocaleString('en-CA', {maximumFractionDigits: 0})}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Insurance</span>
                  <span className="font-mono font-medium">
                    ${mortgageMetrics.annualInsurance.toLocaleString('en-CA', {maximumFractionDigits: 0})}
                  </span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between font-semibold">
                  <span>Total Annual</span>
                  <span className="font-mono text-primary">
                    ${mortgageMetrics.totalAnnualCost.toLocaleString('en-CA', {maximumFractionDigits: 0})}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Mortgage Summary Stats */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h4 className="text-lg font-heading font-semibold mb-4">Mortgage Overview</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Payments</p>
                <p className="text-xl font-bold font-mono">
                  {mortgageMetrics.totalPayments}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Cost</p>
                <p className="text-xl font-bold font-mono">
                  ${mortgageMetrics.totalPaid.toLocaleString('en-CA', {maximumFractionDigits: 0})}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Interest Rate</p>
                <p className="text-xl font-bold font-mono">
                  {mortgageMetrics.interestRate}%
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Payoff Date</p>
                <p className="text-xl font-bold">
                  {mortgageMetrics.payoffDate.toLocaleDateString('en-CA', { year: 'numeric', month: 'short' })}
                </p>
              </div>
            </div>
          </div>
          
          {/* Insights */}
          <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              Quick Insights
            </h4>
            <ul className="space-y-1.5 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>
                  You'll pay <strong>${mortgageMetrics.totalInterest.toLocaleString('en-CA', {maximumFractionDigits: 0})}</strong> in interest over the remaining {mortgageMetrics.yearsRemaining} years
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>
                  Your home equity is <strong>${mortgageMetrics.equity.toLocaleString('en-CA', {maximumFractionDigits: 0})}</strong> ({(100 - mortgageMetrics.ltv).toFixed(1)}% of home value)
                </span>
              </li>
              {mortgageMetrics.ltv <= 80 && (
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  <span>
                    Your LTV is {mortgageMetrics.ltv.toFixed(1)}%, which may qualify you for HELOC or refinancing options
                  </span>
                </li>
              )}
            </ul>
          </div>
          
          {/* Action Button */}
          <div className="flex justify-center mt-6">
            <button
              onClick={() => setShowCompare(true)}
              className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
            >
              <RefreshCw className="h-5 w-5" />
              Compare Scenarios
            </button>
          </div>
        </div>
      )}
      
      {/* Amortization Schedule */}
      {showSummary && mortgageMetrics && (
        <div className="mt-6">
          <AmortizationSchedule
            currentBalance={parseFloat(formData.current_balance)}
            interestRate={parseFloat(formData.current_rate)}
            yearsRemaining={parseInt(formData.amortization_remain)}
            paymentFrequency={formData.payment_frequency}
          />
        </div>
      )}
      
      {/* Compare Scenarios Modal */}
      {showCompare && (
        <CompareScenarios
          mortgage={formData}
          onClose={() => setShowCompare(false)}
        />
      )}
    </div>
  );
};

export default DetailsTab;